/**
 * Pick the finished 1v1s the Last results card shows (specs/home-page AC-39): newest first, at most
 * two, each with the outcome, both totals and the coin swing. Pure selector over the Arena's finished
 * preview list; points and outcome follow the result modal's rules (`buildGameResultItem`) so Home and
 * the modal never disagree about who won.
 */

import type { ChallengeWithStats } from '../../../types/challenges';
import { normalizeUserIdForComparison } from '../../challenges/helpers/challengeCardParticipant.helper';
import { getUserName } from '../../challenges/helpers/challengeCardUi.helper';
import { HOME_RESULTS_LIMIT } from '../constants/home.constants';
import { avatarFor, pointsFor, type ChallengeWithParticipantMaps } from './homeChallengeTiles.helper';
import type { HomeResultOutcome, HomeResultTileModel } from '../types/home.types';

const sameUser = (a: string, b: string) => normalizeUserIdForComparison(a) === normalizeUserIdForComparison(b);

const isCompleted = (c: ChallengeWithStats): boolean => String(c.status).toUpperCase() === 'COMPLETED';

/** A finished head-to-head the viewer took part in: no group, exactly two participants, one of them me. */
export function isFinishedOneVsOne(challenge: ChallengeWithStats, currentUserId: string): boolean {
  if (!isCompleted(challenge) || challenge.groupId) return false;
  const ids = challenge.participantIds ?? [];
  return ids.length === 2 && ids.some((id) => sameUser(id, currentUserId));
}

const finishedAtMs = (c: ChallengeWithStats): number => {
  const t = new Date(c.completedAt ?? c.updatedAt).getTime();
  return Number.isFinite(t) ? t : 0;
};

export function outcomeFor(mine: number, theirs: number): HomeResultOutcome {
  if (mine > theirs) return 'won';
  if (mine < theirs) return 'lost';
  return 'draw';
}

/** +stake on a win, −stake on a loss, nothing on a draw: the swing the result modal shows. */
export function coinsDeltaFor(outcome: HomeResultOutcome, stakeCoins: number): number {
  if (outcome === 'won') return stakeCoins;
  if (outcome === 'lost') return -stakeCoins;
  return 0;
}

export function buildResultTileModel(
  challenge: ChallengeWithParticipantMaps,
  currentUserId: string,
): HomeResultTileModel | null {
  const rivalId = (challenge.participantIds ?? []).find((id) => !sameUser(id, currentUserId));
  if (!rivalId) return null;

  const mine = Number(challenge.myPoints ?? pointsFor(challenge, currentUserId));
  const theirs =
    challenge.leader?.userId && sameUser(challenge.leader.userId, rivalId)
      ? Number(challenge.leader.points ?? 0)
      : pointsFor(challenge, rivalId);
  const outcome = outcomeFor(mine, theirs);
  const name = getUserName(rivalId, challenge.participantDisplayNamesById, challenge.participantEmailsById);

  return {
    challengeId: challenge.id,
    opponent: avatarFor(challenge, rivalId, name),
    outcome,
    myPoints: mine,
    theirPoints: theirs,
    coinsDelta: coinsDeltaFor(outcome, challenge.stakeCoins ?? 0),
  };
}

/** Newest finished 1v1s first, capped at `limit`; groups and unfinished challenges are skipped. */
export function selectLastResults(
  challenges: ChallengeWithStats[],
  currentUserId: string,
  limit: number = HOME_RESULTS_LIMIT,
): HomeResultTileModel[] {
  return challenges
    .filter((c) => isFinishedOneVsOne(c, currentUserId))
    .sort((a, b) => finishedAtMs(b) - finishedAtMs(a))
    .slice(0, limit)
    .map((c) => buildResultTileModel(c as ChallengeWithParticipantMaps, currentUserId))
    .filter((m): m is HomeResultTileModel => m != null);
}
